import type {
  AcceptQuoteResult,
  BookNeedSchema,
  CreateQuoteSchema,
  EventNeeds,
  ExpenseItemWithRelations,
  Quote,
  RecordQuoteSchema,
  RequestQuotesResult,
  RequestQuotesSchema,
} from '@peles/shared';
import { apiClient } from './client';

/** Every need of an event — sourcing and booked — for the Plan view. */
export async function getEventNeeds(eventId: string): Promise<EventNeeds> {
  const { data } = await apiClient.get<EventNeeds>(`/events/${eventId}/needs`);
  return data;
}

export async function listQuotes(needId: string): Promise<Quote[]> {
  const { data } = await apiClient.get<Quote[]>(`/expenses/${needId}/quotes`);
  return data;
}

/** Ask vendors for a quote: a letter each, with an upload link on their quote. */
export async function requestQuotes(
  needId: string,
  body: Omit<RequestQuotesSchema, 'includeItems'> & { includeItems?: boolean },
): Promise<RequestQuotesResult> {
  const { data } = await apiClient.post<RequestQuotesResult>(
    `/expenses/${needId}/quotes/request`,
    body,
  );
  return data;
}

/** A quote that arrived outside a request. */
export async function createQuote(needId: string, body: CreateQuoteSchema): Promise<Quote> {
  const { data } = await apiClient.post<Quote>(`/expenses/${needId}/quotes`, body);
  return data;
}

/** Record figures on a quote; a later answer keeps the earlier as a revision. */
export async function recordQuote(quoteId: string, body: RecordQuoteSchema): Promise<Quote> {
  const { data } = await apiClient.patch<Quote>(`/quotes/${quoteId}`, body);
  return data;
}

export async function acceptQuote(
  quoteId: string,
  body: { notifyOthers: boolean },
): Promise<AcceptQuoteResult> {
  const { data } = await apiClient.post<AcceptQuoteResult>(`/quotes/${quoteId}/accept`, body);
  return data;
}

export async function declineQuote(quoteId: string): Promise<Quote> {
  const { data } = await apiClient.post<Quote>(`/quotes/${quoteId}/decline`);
  return data;
}

export async function withdrawQuote(quoteId: string): Promise<Quote> {
  const { data } = await apiClient.post<Quote>(`/quotes/${quoteId}/withdraw`);
  return data;
}

/** Book a need directly — nothing to shop for. */
export async function bookNeed(
  needId: string,
  body: BookNeedSchema,
): Promise<ExpenseItemWithRelations> {
  const { data } = await apiClient.post<ExpenseItemWithRelations>(`/expenses/${needId}/book`, body);
  return data;
}
